/**
 * Global rate gate for on-demand Bunny status refreshes.
 *
 * The backend rate-limits `POST /videos/{uuid}/bunny-status/refresh` at 6/min
 * **per video**, which does not constrain the case that actually matters: one
 * creator with many videos processing at once. A bulk run of 50 uploads (real
 * — one creator did 51 in a day on 2026-08-29) would sail past every per-video
 * limit while putting a burst on an API whose read limits Bunny does not
 * publish. Only a client-side global cap bounds that.
 *
 * Priority is by attention, not fairness. In a bulk upload the creator is
 * looking at one video, not fifty; the rest can wait for the BE reaper, which
 * closes the same gap every 5 minutes anyway. So a video with the modal open
 * outranks everything else.
 *
 * Two tiers, deliberately. A third — `visible`, for a row on screen in the
 * studio table — was defined here from the start and never assigned by any
 * caller: the only site that builds a candidate has always been a binary
 * `hasOpenModal ? focused : background`. Wiring it would need viewport
 * observation on every content row to buy a distinction the policy above says
 * we do not want, so it was deleted rather than implemented (FRONT-438).
 */

/** Refreshes per second across every video, regardless of how many are queued. */
const MAX_REFRESHES_PER_SECOND = 1;

const MIN_INTERVAL_MS = 1000 / MAX_REFRESHES_PER_SECOND;

/** Higher wins. A creator watching one upload gets the slot. */
export const REFRESH_PRIORITY = {
  /** Upload modal is open on this video. */
  focused: 3,
  /** Processing in the background. */
  background: 1,
} as const;

export type RefreshPriority = (typeof REFRESH_PRIORITY)[keyof typeof REFRESH_PRIORITY];

export interface RefreshCandidate {
  uploadId: string;
  priority: RefreshPriority;
  /** Epoch ms the candidate became eligible; older wins ties. */
  eligibleSince: number;
}

/**
 * What the gate did between one issued refresh and the next. Rides on the
 * winner's `studio_upload_status_refreshed` rather than firing per suppression:
 * a 50-video bulk run polls far faster than 1/sec, so an event per closed gate
 * would put the same burst on PostHog that this queue exists to keep off Bunny.
 */
export interface RefreshTakeStats {
  /**
   * Takes refused by the 1/sec gate since the last issued refresh. Counted only
   * when candidates existed — that is what separates "the cap is biting" from
   * "nothing was eligible", which were the same `null` before FRONT-438.
   */
  suppressed: number;
  /** Videos eligible at the moment this refresh won its slot. Queue depth. */
  candidates: number;
}

export interface RefreshQueue {
  /**
   * Picks at most one candidate to refresh now, or null when the rate gate is
   * closed or nothing is eligible. One per tick by design — the cap is the
   * point, and draining a backlog in a burst would defeat it.
   */
  take: (candidates: RefreshCandidate[], now?: number) => RefreshCandidate | null;
  /** Test seam / diagnostics. */
  lastIssuedAt: () => number | null;
  /** Gate behaviour behind the most recent successful `take`. */
  lastTakeStats: () => RefreshTakeStats;
}

function byPriorityThenAge(a: RefreshCandidate, b: RefreshCandidate): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  return a.eligibleSince - b.eligibleSince;
}

export function createRefreshQueue(): RefreshQueue {
  let lastIssued: number | null = null;
  let suppressedSinceIssue = 0;
  let lastStats: RefreshTakeStats = { suppressed: 0, candidates: 0 };

  return {
    take: (candidates, now = Date.now()) => {
      if (candidates.length === 0) return null;
      if (lastIssued !== null && now - lastIssued < MIN_INTERVAL_MS) {
        suppressedSinceIssue += 1;
        return null;
      }

      // Slice before sorting: the caller's array is React-owned state in some
      // call paths and must not be mutated in place.
      const [winner] = [...candidates].sort(byPriorityThenAge);
      if (!winner) return null;

      lastIssued = now;
      lastStats = { suppressed: suppressedSinceIssue, candidates: candidates.length };
      suppressedSinceIssue = 0;
      return winner;
    },

    lastIssuedAt: () => lastIssued,
    lastTakeStats: () => lastStats,
  };
}

export const REFRESH_QUEUE_BOUNDS = {
  MAX_REFRESHES_PER_SECOND,
  MIN_INTERVAL_MS,
} as const;
